import { ColorsetDefaultsService } from '../../../services/colorset/colorset-defaults.service';
import { Component, effect, inject } from '@angular/core';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { IndicatorData } from '../../../models/indicator-data';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AppUtilityService } from '../../../utility/app.utility';
import { RoutingService } from '../../../services/routing.service';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { KeyValueDistricts } from '../../../models/geo-data';
import { DividerModule } from 'primeng/divider';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { InputTextModule } from 'primeng/inputtext';
import { NgForOf } from '@angular/common';
import { GlobalsStore } from '../../../stores/globals.state';
import { SearchInputComponent } from '../../atoms/search-input/search-input.component';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { Indicator } from '../../../models/indicator';
import { DataLayer } from '../../../models/data-layer';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { getState } from '@ngrx/signals';
import { ColorsetDropdownComponent } from '../../atoms/colorset-dropdown/colorset-dropdown.component';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { LegendFormComponent } from '../../molecules/legend-form/legend-form.component';
import { legendModes } from '../../../models/legend';
import { ComparisonValueStore } from '../../../stores/comparison-value/comparison-value.state';
import { Button } from 'primeng/button';
import { HeaderComponent } from '../../atoms/header/header.component';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';

@Component({
  selector: 'app-indicator-data-form',
  standalone: true,
  imports: [
    DividerModule,
    FormFieldWrapperComponent,
    FormsModule,
    InputTextModule,
    ReactiveFormsModule,
    NgForOf,
    SearchInputComponent,
    AlertComponent,
    FormFooterComponent,
    ColorsetDropdownComponent,
    CheckboxModule,
    DropdownModule,
    LegendFormComponent,
    Button,
    RouterLink,
    HeaderComponent,
  ],
  templateUrl: './indicator-data-form.component.html',
  styleUrl: './indicator-data-form.component.scss',
})
export class IndicatorDataFormComponent extends BaseFormComponent<IndicatorData> {
  readonly store = inject(IndicatorDataStore);
  readonly defaultsService = inject(ColorsetDefaultsService);
  readonly layerStore = inject(DataLayerStore);
  readonly comparisonStore = inject(ComparisonValueStore);
  readonly globalStore = inject(GlobalsStore);
  override form: FormGroup = new FormGroup({});
  override itemToEdit?: IndicatorData;
  districts: KeyValueDistricts | null = null;
  dataLayer: DataLayer | null = null;
  indicator: Indicator | null = null;
  filteredData?: FormArray;
  override formInitialized = false;

  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private fb: FormBuilder
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );

    effect(() => {
      const state = getState(this.store);
      const globalStoreState = getState(this.globalStore);

      this.indicator = globalStoreState.selectedIndicator?.isSet
        ? globalStoreState.selectedChildIndicator
        : globalStoreState.selectedIndicator;
      if (state.selectedId) {
        this.itemToEdit = this.store.selectedItem();
        queueMicrotask(() => {
          if (
            this.itemToEdit &&
            globalStoreState.selectedLayer &&
            globalStoreState.selectedLayer._id !== this.itemToEdit?.dataLayer
          ) {
            this.globalStore.setSelectedLayer(
              this.layerStore.getById(this.itemToEdit?.dataLayer)
            );
          }
          this.initializeForm();
        });
      }
    });
  }

  /**
   * Initializes the form by setting up default values and creating the form structure
   * using FormBuilder. This also retrieves necessary data such as districts and data
   * layers from the global store and binds them to the form fields.
   *
   * @return {void} Does not return any value.
   */
  initializeForm(): void {
    this.districts = this.globalStore.getDistrictsFromSelection();
    this.dataLayer = this.globalStore.selectedLayer();

    this.form = this.fb.group({
      dataLayer: [this.itemToEdit?.dataLayer || this.dataLayer?._id || ''],
      dataLayerType: [
        this.itemToEdit?.dataLayerType || this.dataLayer?.parent || '',
      ],
      data: this.fb.array([]),
      avg: [this.itemToEdit?.avg || ''],
      total: [this.itemToEdit?.total || ''],
      colorSet: [this.itemToEdit?.colorSet],
      invert: [this.itemToEdit?.invert || false],
      legendMode: [this.itemToEdit?.legendMode || 'default'],
      legend: this.fb.array([]),
    });

    queueMicrotask(() => {
      this.renderForm();
      this.formInitialized = true;
    });
  }

  /**
   * Retrieves the 'legend' form array from the form group.
   *
   * @return {FormArray} The form array associated with the key 'legend'.
   */
  public get legendFormArray(): FormArray {
    return this.form.get('legend') as FormArray;
  }

  /**
   * Filters the data in the form array based on the provided search term.
   * If no search term is provided, the full data array is assigned to `filteredData`.
   *
   * @param {string} searchTerm - The term used to filter the data. Matching is case-insensitive.
   * @return {void} This method does not return a value. It updates the `filteredData` property.
   */
  onSearch(searchTerm: string): void {
    const dataArray = this.form.get('data') as FormArray;

    if (!searchTerm) {
      this.filteredData = dataArray;
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      const filtered = dataArray.controls.filter(dataControl => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return Object.values(dataControl.controls).some((control: any) => {
          return String(control.value).toLowerCase().includes(lowerSearchTerm);
        });
      });

      this.filteredData = this.fb.array(filtered);
    }
  }

  /**
   * Renders the form by initializing the filtered data field
   * and adding the required form fields dynamically.
   *
   * @return {void} Does not return a value.
   */
  renderForm(): void {
    this.filteredData = this.form.get('data') as FormArray;
    this.addFormFields();
  }

  /**
   * Retrieves the FormArray instance associated with the 'data' control in the form.
   *
   * @return {FormArray} The FormArray instance of the 'data' control.
   */
  public get dataFormArray(): FormArray {
    return this.form.get('data') as FormArray;
  }

  /**
   * Adds form fields to the data form array by populating it with entries from
   * the comparison store and districts data. Extracts keys and names to dynamically
   * build the form fields.
   *
   * @return {void} No return value.
   */
  addFormFields(): void {
    this.dataFormArray.controls = [];
    const combined = [
      ...this.comparisonStore.withoutDefaultAsArray(),
      ...Object.values(this.districts || {}).sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    ];

    for (const item of combined) {
      this.addDataEntry(
        this.itemToEdit?.data?.[item.key] ?? null,
        item.key,
        item.name
      );
    }
  }

  /**
   * Adds a new data entry to the form array with the specified value, key, and name.
   *
   * @param {number|null} value - The value to be added, can be a number or null. Defaults to an empty string if null or undefined.
   * @param {string} key - The key associated with the data entry. Defaults to an empty string if undefined.
   * @param {string} name - The name associated with the data entry. Defaults to an empty string if undefined.
   * @return {void} Does not return a value.
   */
  addDataEntry(value: number | null, key: string, name: string): void {
    const dataGroup = this.fb.group({
      value: [value ?? ''],
      key: [key || ''],
      name: [name || ''],
    });

    this.dataFormArray.push(dataGroup);
  }

  /**
   * Handles form submission by transforming the form's value structure
   * and passing the modified value to the submit function.
   *
   * @return {void} No return value.
   */
  onSubmit(): void {
    const overrideFormValue = this.form.value;
    const data: any = {};

    this.form.value.indicator =
      this.itemToEdit?.indicator || this.indicator?._id || '';
    this.form.value.data.forEach((dataItem: any) => {
      data[dataItem.key] = dataItem.value === '' ? null : dataItem.value;
    });
    overrideFormValue.data = data;
    this.submit(overrideFormValue);
  }

  protected readonly legendModes = legendModes;
}
